import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { Box, CheckIcon, CloseIcon, HStack, Pressable, ScrollView, VStack, WarningIcon, useToast } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Button, PencilIcon, Text } from '@pimm/base';
import { UpdateCustomerStoreHours } from '@pimm/services/lib/sms-tenants/services';
import { useStoreSettings } from '../context/store-settings.context';
import { DailyHoursShape, EditValues, ActiveCell, ConfirmModalState } from '../types';
import { parseApiStoreHours, toEditValues, computeDirtySet, buildPayload, formatLastModified } from '../utils';
import { CellEditModal } from './cell-edit-modal';
import { ConfirmModal } from './confirm-modal';
import { ContactSupportLink } from './contact-support-link';
import { HoursCard } from './hours-card';
import { ReadOnlyPopover } from './read-only-popover';

export type StoreHoursPageHandle = {
  requestNavigate: (action: () => void) => void;
};

export const StoreHoursPage = forwardRef<StoreHoursPageHandle>((_, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const site = useStoreSettings(_ => _.site);
  const isEditEnabled = useStoreSettings(_ => _.isStoreHoursEditable);
  const onConfigChange = useStoreSettings(_ => _.onConfigChange);
  const onSupportPress = useStoreSettings(_ => _.onSupportPress);
  const apiDays = site.config?.storeHoursConfig?.dailyOpHours ?? [];
  const firstDayOfWeek: number = site.config?.storeHoursConfig?.firstDayOfWeek ?? 1;

  const [storeHours, setStoreHours] = useState<DailyHoursShape[]>(() => (apiDays.length ? parseApiStoreHours(apiDays) : []));
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState<EditValues | null>(null);
  const [originalValues, setOriginalValues] = useState<EditValues | null>(null);
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [showROPopover, setShowROPopover] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastModifiedOverride, setLastModifiedOverride] = useState<string | null>(null);

  // Re-initialise when the site focus changes.
  useEffect(() => {
    const days = site.config?.storeHoursConfig?.dailyOpHours ?? [];
    setStoreHours(days.length ? parseApiStoreHours(days) : []);
    setLastModifiedOverride(null);
    if (editMode) exitEditMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);

  const lastModifiedIso = lastModifiedOverride ?? site.lastModifiedOnUtc;

  const dirtySet = useMemo(() => computeDirtySet(editValues, originalValues), [editValues, originalValues]);
  const hasPendingChanges = dirtySet.size > 0;
  const changedCount = dirtySet.size;

  const closedDayIndices = useMemo(() => new Set(storeHours.map((h, i) => (h.isClosed ? i : -1)).filter(i => i >= 0)), [storeHours]);

  // firstDayOfWeek is in DOP space (0=Sun,1=Mon…6=Sat).
  // Internal order: Mon=0…Sun=6. Convert: startProto = dow===0 ? 6 : dow-1.
  const orderedIndices = useMemo(() => {
    const startProto = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    return Array.from({ length: 7 }, (_, i) => (startProto + i) % 7);
  }, [firstDayOfWeek]);

  const displayHours: DailyHoursShape[] = useMemo(
    () => (editMode && editValues ? storeHours.map((h, i) => ({ ...h, ...editValues[i] })) : storeHours),
    [storeHours, editValues, editMode],
  );

  const enterEditMode = () => {
    const snapshot = toEditValues(storeHours);
    setEditValues(snapshot);
    setOriginalValues(snapshot);
    setEditMode(true);
  };

  const exitEditMode = () => {
    setEditMode(false);
    setEditValues(null);
    setOriginalValues(null);
    setActiveCell(null);
    setShowROPopover(false);
    setSaveError(null);
  };

  // Exit edit mode automatically when permission is revoked.
  useEffect(() => {
    if (!isEditEnabled && editMode) exitEditMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditEnabled]);

  const guardNavigation = (action: () => void) => {
    if (editMode && hasPendingChanges) {
      setConfirmModal({ type: 'nav', action });
    } else {
      if (editMode) exitEditMode();
      action();
    }
  };

  useImperativeHandle(ref, () => ({ requestNavigate: guardNavigation }));

  const handleExitEditMode = () => {
    if (hasPendingChanges) setConfirmModal({ type: 'exit' });
    else exitEditMode();
  };

  const handleCancel = () => {
    if (hasPendingChanges) setConfirmModal({ type: 'discard' });
    else {
      setEditValues(toEditValues(storeHours));
      setActiveCell(null);
    }
  };

  const handleConfirmConfirm = () => {
    const modal = confirmModal!;
    setConfirmModal(null);
    if (modal.type === 'discard') {
      setEditValues(toEditValues(storeHours));
      setActiveCell(null);
    } else if (modal.type === 'exit') {
      exitEditMode();
    } else if (modal.type === 'nav') {
      exitEditMode();
      modal.action?.();
    }
  };

  const saveHoursMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof buildPayload>) =>
      UpdateCustomerStoreHours(site!.id!, payload as any),
  });

  const handleSave = async () => {
    if (!site || !editValues) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await saveHoursMutation.mutateAsync(buildPayload(editValues, storeHours));
      const updated = storeHours.map((h, i) => ({ ...h, ...editValues![i] }));
      setStoreHours(updated);
      const now = new Date().toISOString();
      setLastModifiedOverride(now);
      onConfigChange({ ...site, lastModifiedOnUtc: now });
      exitEditMode();
      toast.show({
        placement: 'top',
        duration: 2500,
        render: () => (
          <HStack
            alignItems="center"
            space={2}
            px={3}
            py={2}
            bg="success.50"
            borderWidth={1}
            borderColor="success.300"
            rounded="md"
            shadow={2}
          >
            <CheckIcon size={4} color="success.700" />
            <Text size="sm" fontWeight={600} color="success.700">
              {t('store_settings:store_hours_updated_toast')}
            </Text>
          </HStack>
        ),
      });
    } catch {
      setSaveError(t('store_settings:store_hours_save_failed_banner'));
    } finally {
      setIsSaving(false);
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <VStack flex={1} overflow="hidden">
      {/* Right-pane header */}
      <HStack
        alignItems="center"
        justifyContent="space-between"
        px={4}
        py={3}
        bg="white"
        borderBottomWidth={1}
        borderColor="gray.200"
        flexShrink={0}
        flexWrap="wrap"
        space={3}
      >
        <HStack alignItems="center" space={3}>
          <Text size="2xl" fontWeight={700} color="black">
            {t('store_settings:store_hours')}
          </Text>
          <Text size="xs" color="gray.500">
            {formatLastModified(lastModifiedIso, t('store_settings:last_modified'))}
          </Text>
        </HStack>

        {editMode ? (
          <HStack alignItems="center" space={3} flexWrap="wrap">
            {hasPendingChanges && !isSaving && (
              <HStack alignItems="center" space={1.5}>
                <Box w={1.5} h={1.5} rounded="full" bg="blue.500" />
                <Text size="sm" fontWeight={500} color="blue.700">
                  {changedCount} {t(changedCount === 1 ? 'store_settings:unsaved_changes_one' : 'store_settings:unsaved_changes_other')}
                </Text>
              </HStack>
            )}
            {hasPendingChanges && (
              <Button size="sm" alternate outline minW={90} disabled={isSaving} _text={{ fontWeight: 600 }} onPress={handleCancel}>
                {t('store_settings:discard')}
              </Button>
            )}
            <Button
              size="sm"
              minW={90}
              isLoading={isSaving}
              disabled={!hasPendingChanges || isSaving}
              _text={{ fontWeight: 600 }}
              onPress={handleSave}
            >
              {t('store_settings:save')}
            </Button>
            <Box w="1px" h={4} bg="gray.200" />
            <Pressable onPress={handleExitEditMode} disabled={isSaving} opacity={isSaving ? 0.5 : 1}>
              <HStack alignItems="center" px={3} py={1.5} rounded="lg" borderWidth={1} borderColor="gray.300" bg="gray.800">
                <Text size="sm" fontWeight={600} color="white">
                  {t('store_settings:exit_edit_mode')}
                </Text>
              </HStack>
            </Pressable>
          </HStack>
        ) : (
          <HStack alignItems="center" space={3}>
            <ContactSupportLink onPress={onSupportPress} />
            {isEditEnabled && (
              <Pressable onPress={enterEditMode}>
                <HStack
                  alignItems="center"
                  space={1}
                  px={3}
                  py={1.5}
                  rounded="lg"
                  bg="primary.500"
                  borderWidth={1}
                  borderColor="primary.500"
                >
                  <PencilIcon size={3} color="black" />
                  <Text size="sm" fontWeight={600} color="black">
                    {t('store_settings:edit_hours')}
                  </Text>
                </HStack>
              </Pressable>
            )}
          </HStack>
        )}
      </HStack>

      {/* Save error banner */}
      {saveError && (
        <HStack
          alignItems="center"
          justifyContent="space-between"
          px={4}
          py={2}
          bg="error.50"
          borderBottomWidth={1}
          borderColor="error.200"
          flexShrink={0}
        >
          <HStack alignItems="center" space={2} flex={1}>
            <WarningIcon size={4} color="error.700" />
            <Text size="sm" color="error.700" flex={1}>
              {saveError}
            </Text>
          </HStack>
          <Pressable onPress={() => setSaveError(null)}>
            <CloseIcon size={3} color="error.700" />
          </Pressable>
        </HStack>
      )}

      {/* First Day of Week metadata bar */}
      <HStack alignItems="center" px={4} py={2} bg="white" borderBottomWidth={1} borderColor="gray.200" flexShrink={0} space={2}>
        <Text size="sm" color="gray.500">
          {t('store_settings:first_day_of_the_week')}
        </Text>
        <Box px={2} py={0.5} rounded="full" bg="gray.100" borderWidth={1} borderColor="gray.200">
          <Text size="sm" fontWeight={500} color="gray.600">
            {dayNames[firstDayOfWeek]}
          </Text>
        </Box>
      </HStack>

      {/* Scrollable grid area */}
      {storeHours.length === 0 ? (
        <VStack flex={1} alignItems="center" justifyContent="center" bg="grayCool.100" space={2}>
          <WarningIcon size={6} color="gray.400" />
          <Text size="md" fontWeight={600} color="gray.700">
            {t('store_settings:store_hours_unavailable_title')}
          </Text>
          <Text size="sm" color="gray.500" textAlign="center" maxW="320px">
            {t('store_settings:store_hours_unavailable_body')}
          </Text>
        </VStack>
      ) : (
        <ScrollView flex={1} bg="grayCool.100" showsVerticalScrollIndicator={false}>
          <VStack px={3} py={3} space={3}>
            <HoursCard
              title={t('store_settings:store_operating_hours')}
              rows={[
                { type: 'toggle', label: t('store_settings:closed_non_op'), field: 'isClosed' },
                { type: 'toggle', label: t('store_settings:open_twentyfour_hours'), field: 'open24Hrs' },
                { type: 'editable', rowKey: 'diningRoom' },
                { type: 'editable', rowKey: 'driveThru' },
              ]}
              storeHours={storeHours}
              displayHours={displayHours}
              editMode={editMode}
              activeCell={activeCell}
              dirtySet={dirtySet}
              onCellClick={(rowKey, dayIdx) => {
                setShowROPopover(false);
                setActiveCell({ rowKey, dayIdx });
              }}
              onReadOnlyClick={() => {
                setActiveCell(null);
                setShowROPopover(true);
              }}
              closedDayIndices={closedDayIndices}
              orderedIndices={orderedIndices}
            />

            <HoursCard
              title={t('store_settings:food_service_hours')}
              rows={[
                { type: 'editable', rowKey: 'breakfast' },
                { type: 'editable', rowKey: 'lunch' },
                { type: 'editable', rowKey: 'dinner' },
              ]}
              storeHours={storeHours}
              displayHours={displayHours}
              editMode={editMode}
              activeCell={activeCell}
              dirtySet={dirtySet}
              onCellClick={(rowKey, dayIdx) => {
                setShowROPopover(false);
                setActiveCell({ rowKey, dayIdx });
              }}
              onReadOnlyClick={() => {
                setActiveCell(null);
                setShowROPopover(true);
              }}
              closedDayIndices={closedDayIndices}
              orderedIndices={orderedIndices}
            />
          </VStack>
        </ScrollView>
      )}

      {/* Overlay layer */}
      {activeCell && editMode && editValues && (
        <CellEditModal
          cell={activeCell}
          storeHours={storeHours}
          editValues={editValues}
          setEditValues={setEditValues}
          originalValues={originalValues!}
          onClose={() => setActiveCell(null)}
        />
      )}

      {showROPopover && editMode && <ReadOnlyPopover onClose={() => setShowROPopover(false)} />}

      {confirmModal?.type === 'discard' && (
        <ConfirmModal
          title={t('store_settings:discard_changes_title')}
          body={t('store_settings:discard_changes_body')}
          confirmLabel={t('store_settings:discard_changes_confirm')}
          cancelLabel={t('store_settings:keep_editing')}
          onConfirm={handleConfirmConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {(confirmModal?.type === 'exit' || confirmModal?.type === 'nav') && (
        <ConfirmModal
          title={t('store_settings:leave_without_saving_title')}
          body={t('store_settings:leave_without_saving_body')}
          confirmLabel={t('store_settings:leave_page')}
          cancelLabel={t('store_settings:stay')}
          onConfirm={handleConfirmConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </VStack>
  );
});

StoreHoursPage.displayName = 'StoreHoursPage';
